//src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from "react-hot-toast";
import Login from './components/Login';
import Home from './components/Home';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Orders from './components/Orders';
import Errors from './components/Errors';
import OrderDetails from './components/tables/OrderDetails'; 
import OrderUpdate from './components/tables/OrderUpdate'; 
import CreateOrder from './components/tables/CreateOrders'; 
import Dashboard from './components/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import ProductsTable from './components/tables/ProductsTable';
import ProductForm from './components/products/ProductForm';
import QrPage from "./components/products/QrGenerator";
import QrList from "./components/tables/QrListTable";
import ProductView from './components/products/ProductView';
import Bodega from './components/Bodega';
import Empacado from './components/bodega/Empacado';
import Enviado from './components/bodega/Enviado';
import PickingIniciado from './components/bodega/PickingIniciado';
import BodegaDevolucion from './components/bodega/BodegaDevolucion';
import Exportar from './components/bodega/Exportar';
import PermissionsManagement from './components/settings/PermissionsManagement';
import { MercadoLibreToken } from './components/integracion-mercadolibre';
import ProtectedRoute from './components/common/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setGlobalLogoutCallback } from './utils/authUtils';
import Users from './pages/Users';
import PendingOrders from './pages/PendingOrders';
import PendingOrdersForm from './components/forms/PendingOrdersForm';
import VerifyTransactionsForm from './components/forms/VerifyTransactionsForm';
import Tutorials from './components/Tutorials';
import Cotizacion from './components/Cotizacion';
import CreateCotizacion from './components/tables/CreateCotizacion';
import CotizacionDetails from './components/tables/CotizacionDetails';
import CotizacionUpdate from './components/tables/CotizacionUpdate';
import ProductsImporter from './components/products/ProductsImporter';
import SeguimientoEnvios from './pages/envio/SeguimientoEnvios';
import LiquidateShipping from './pages/envio/LiquidateShipping';
import Manifiestos from './pages/envio/Manifiestos';
import CancelarEnvios from './pages/CancelarEnvios';
import VerifyTransactions from './pages/VerifyTransactions';
import EntregaPDV from './pages/EntregaPDV';
import EntregaPDVForm from './components/forms/EntregaPDVForm';
import DespachosBodega from './pages/DespachosBodega';
import FalabellaIntegration from './pages/FalabellaIntegration';
import HomeCenterSync from './pages/HomeCenterSync';
import ComparativeTablePage from './pages/ComparativeTablePage';
import RemisionesPorSKUPage from './pages/RemisionesPorSKUPage';
import PedidosOfimaPage from './pages/PedidosOfimaPage';
import MarketplaceProducts from './pages/MarketplaceProducts';
import CronTasks from './pages/cron/CronTasks';
import FlexyTrasladosListPage from './pages/flexy/FlexyTrasladosListPage';
import FlexyTrasladoCreatePage from './pages/flexy/FlexyTrasladoCreatePage';
import PedidosEnDevolucion from './pages/devoluciones/PedidosEnDevolucion';
import DevolucionesVerificadas from './pages/devoluciones/DevolucionesVerificadas';

/**
 * Componente interno que configura el callback de logout global
 * Debe estar dentro del Router para poder usar useNavigate
 */
const AppRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    // Configurar el callback global de logout para que use React Router
    setGlobalLogoutCallback(() => {
      logout();
      navigate('/login', { replace: true });
    });
  }, [navigate, logout]);

  return <>{children}</>;
};

const App: React.FC = () => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('accessToken'));

  const handleLogin = (newToken: string, role: string) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    setToken(null);
  };

  return (
    <AuthProvider>
      <Router>
        <AppRouter>
          <Toaster position="top-center" />
          <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/" element={<PrivateRoute token={token}>
            <Dashboard onLogout={handleLogout} />
          </PrivateRoute>}>
            <Route path="home" element={<Home />} />
            <Route path="profile" element={<Profile />} />
            <Route path="tutorials" element={<Tutorials />} />
            <Route path="settings" element={
              <ProtectedRoute 
                requiredPermission="view_settings"
                fallbackMessage="No tienes permisos para acceder a la configuración"
              >
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="settings/permissions" element={
              <ProtectedRoute 
                requiredPermission="view_permissions"
                fallbackMessage="No tienes permisos para acceder a la gestión de permisos"
              >
                <PermissionsManagement />
              </ProtectedRoute>
            } />
            <Route path="settings/users" element={
              <ProtectedRoute 
                requiredPermission="view_users"
                fallbackMessage="No tienes permisos para acceder a la gestión de usuarios"
              >
                <Users />
              </ProtectedRoute>
            } />
            <Route path="settings/mercadolibre-token" element={
              <ProtectedRoute 
                requiredPermission="view_mercadolibre_token"
                fallbackMessage="No tienes permisos para acceder a la configuración de tokens"
              >
                <MercadoLibreToken />
              </ProtectedRoute>
            } />
            <Route path="settings/cron-tasks" element={
              <ProtectedRoute 
                requiredPermission="view_cron_tasks"
                fallbackMessage="No tienes permisos para acceder a las tareas cron"
              >
                <CronTasks />
              </ProtectedRoute>
            } />
            <Route path="orders" element={
              <ProtectedRoute 
                requiredPermission="view_orders"
                fallbackMessage="No tienes permisos para ver los pedidos"
              >
                <Orders />
              </ProtectedRoute>
            } />
            <Route path="envios/pending" element={<PendingOrders />}>
              <Route index element={<PendingOrdersForm />} />
              <Route path=":id" element={<PendingOrdersForm />} />
            </Route>
            <Route path="orders/verify-transactions" element={<VerifyTransactions />}>
              <Route index element={<VerifyTransactionsForm />} />
              <Route path=":id" element={<VerifyTransactionsForm />} />
            </Route>
            <Route path="orders/entrega-pdv" element={<EntregaPDV />}>
              <Route index element={<EntregaPDVForm />} />
              <Route path=":id" element={<EntregaPDVForm />} />
            </Route>
            <Route path="orders/despachos-bodega" element={
              <ProtectedRoute
                requiredPermission="manage_despachos_bodega"
                fallbackMessage="No tienes permisos para acceder a Despachos bodega"
              >
                <DespachosBodega />
              </ProtectedRoute>
            } />
            <Route path="orders/sync-homecenter" element={
              <ProtectedRoute 
                requiredPermission="sync_homecenter"
                fallbackMessage="No tienes permisos para sincronizar pedidos de Home Center"
              >
                <HomeCenterSync />
              </ProtectedRoute>
            } />
            <Route path="erp/comparative-table" element={
              <ProtectedRoute 
                requiredPermission="view_comparative_table"
                fallbackMessage="No tienes permisos para acceder a la tabla comparativa"
              >
                <ComparativeTablePage />
              </ProtectedRoute>
            } />
            <Route path="erp/remisiones-sku" element={
              <ProtectedRoute 
                requiredPermission="view_orders"
                fallbackMessage="No tienes permisos para acceder a las remisiones por SKU"
              >
                <RemisionesPorSKUPage />
              </ProtectedRoute>
            } />
            <Route path="erp/pedidos-ofima" element={
              <ProtectedRoute
                requiredPermission="view_erp"
                fallbackMessage="No tienes permisos para consultar pedidos Ofima"
              >
                <PedidosOfimaPage />
              </ProtectedRoute>
            } />
            <Route path="flexy/traslados" element={
              <ProtectedRoute
                requiredPermission="view_wms_traslados"
                fallbackMessage="No tienes permisos para ver transferencias Flexy"
              >
                <FlexyTrasladosListPage />
              </ProtectedRoute>
            } />
            <Route path="flexy/traslados/new" element={
              <ProtectedRoute
                requiredPermission="manage_wms_traslados"
                fallbackMessage="No tienes permisos para crear transferencias Flexy"
              >
                <FlexyTrasladoCreatePage />
              </ProtectedRoute>
            } />
            <Route path="devoluciones" element={<Navigate to="/devoluciones/pedidos" replace />} />
            <Route path="devoluciones/pedidos" element={
              <ProtectedRoute
                requiredPermission="view_orders"
                fallbackMessage="No tienes permisos para consultar pedidos en devolución"
              >
                <PedidosEnDevolucion />
              </ProtectedRoute>
            } />
            <Route path="devoluciones/verificadas" element={
              <ProtectedRoute
                requiredPermission="view_orders"
                fallbackMessage="No tienes permisos para consultar devoluciones para transferir"
              >
                <DevolucionesVerificadas />
              </ProtectedRoute>
            } />
            <Route path="devoluciones/verificadas/:id" element={
              <ProtectedRoute
                requiredPermission="view_orders"
                fallbackMessage="No tienes permisos para consultar devoluciones para transferir"
              >
                <DevolucionesVerificadas />
              </ProtectedRoute>
            } />
            <Route path="Errors" element={<Errors />} />
            <Route path="list/:id" element={<OrderDetails />} />  
            <Route path="edit/:id" element={<OrderUpdate/>} /> 
            <Route path="orders/new" element={<CreateOrder/>} /> 
            <Route path="/products" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para ver los productos"
              >
                <ProductsTable />
              </ProtectedRoute>
            } />
            <Route path="/products/new" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para crear productos"
              >
                <ProductForm />
              </ProtectedRoute>
            } />
            <Route path="/products/edit/:id" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para editar productos"
              >
                <ProductForm />
              </ProtectedRoute>
            } />
            <Route path="/products/view/:id" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para ver detalles de productos"
              >
                <ProductView />
              </ProtectedRoute>
            } />
            <Route path="/products/import" element={
              <ProtectedRoute 
                requiredPermission="manage_products"
                fallbackMessage="No tienes permisos para importar productos"
              >
                <ProductsImporter />
              </ProtectedRoute>
            } />
            <Route path="/qr" element={
              <ProtectedRoute 
                requiredPermission="view_qr"
                fallbackMessage="No tienes permisos para generar códigos QR"
              >
                <QrPage />
              </ProtectedRoute>
            } />
            <Route path="/qr-list" element={
              <ProtectedRoute 
                requiredPermission="view_qr"
                fallbackMessage="No tienes permisos para ver la lista de códigos QR"
              >
                <QrList />
              </ProtectedRoute>
            } />
            <Route path="/falabella" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para ver productos de Falabella"
              >
                <FalabellaIntegration />
              </ProtectedRoute>
            } />
            <Route path="/products/marketplace" element={
              <ProtectedRoute 
                requiredPermission="view_products"
                fallbackMessage="No tienes permisos para ver productos de marketplace"
              >
                <MarketplaceProducts />
              </ProtectedRoute>
            } />
            
            {/* Rutas de Bodega */}
            <Route path="/fulfillment" element={
              <ProtectedRoute 
                requiredPermission="view_bodega"
                fallbackMessage="No tienes permisos para acceder a la bodega"
              >
                <Bodega />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="empacado" replace />} />
              <Route path="empacado" element={<Empacado />} />
              <Route path="enviado" element={<Enviado />} />
              <Route
                path="picking-iniciado"
                element={
                  <ProtectedRoute
                    requiredPermission="manage_piking_iniciado"
                    fallbackMessage="No tienes permisos para cambiar pedidos a Picking Iniciado"
                  >
                    <PickingIniciado />
                  </ProtectedRoute>
                }
              />
              <Route path="bodega-devolucion" element={<BodegaDevolucion />} />
              <Route path="exportar" element={<Exportar />} />
            </Route>
            <Route path="cotizaciones" element={
              <ProtectedRoute 
                requiredPermission="view_cotizaciones"
              fallbackMessage="No tienes permisos para ver las cotizaciones" 
              >
                <Cotizacion />
              </ProtectedRoute>
            } />
            <Route path="cotizaciones/new" element={
              <ProtectedRoute 
                requiredPermission="manage_cotizaciones"
                fallbackMessage="No tienes permisos para crear cotizaciones"
              >
                <CreateCotizacion />
              </ProtectedRoute>
            } />
            <Route path="cotizaciones/:id" element={
              <ProtectedRoute 
                requiredPermission="view_cotizaciones"
                fallbackMessage="No tienes permisos para ver la cotización"
              >
                <CotizacionDetails />
              </ProtectedRoute>
            } />
            <Route path="cotizaciones/:id/update" element={
              <ProtectedRoute 
                requiredPermission="manage_cotizaciones"
                fallbackMessage="No tienes permisos para editar cotizaciones"
              >
                <CotizacionUpdate />
              </ProtectedRoute>
            } />
            <Route path="cotizaciones/:id/edit" element={
              <ProtectedRoute 
                requiredPermission="manage_cotizaciones"
                fallbackMessage="No tienes permisos para editar cotizaciones"
              >
                <CotizacionUpdate />
              </ProtectedRoute>
            } />
            {/* Menú Envíos */}
            <Route path="envios/seguimiento" element={<SeguimientoEnvios />} />
            <Route path="envios/liquidar/*" element={<LiquidateShipping />} />
            <Route path="envios/cancelar" element={
              <ProtectedRoute 
                requiredPermission="manage_envios"
                fallbackMessage="No tienes permisos para cancelar envíos"
              >
                <CancelarEnvios />
              </ProtectedRoute>
            } />
            <Route path="envios/manifiestos" element={
              <ProtectedRoute
                requiredPermission="manage_envios"
                fallbackMessage="No tienes permisos para generar manifiestos"
              >
                <Manifiestos />
              </ProtectedRoute>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
        </AppRouter>
      </Router>
    </AuthProvider>
  );
};

export default App;